/**
 * Export utilities for compliance reporting and municipal record-keeping.
 * Supports CSV and basic PDF (print-optimized HTML) exports.
 */

export interface ExportColumn {
  header: string;
  key: string;
}

/** Convert an array of objects to a CSV string */
function toCSV(columns: ExportColumn[], rows: Record<string, unknown>[]): string {
  const header = columns.map((c) => `"${c.header}"`).join(',');
  const body = rows.map((row) =>
    columns
      .map((c) => {
        const val = row[c.key] ?? '';
        const str = Array.isArray(val) ? val.join('; ') : String(val);
        return `"${str.replace(/"/g, '""')}"`;
      })
      .join(',')
  );
  return [header, ...body].join('\r\n');
}

/** Trigger a browser download of a text file */
function downloadText(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Export data as CSV */
export function exportCSV(
  columns: ExportColumn[],
  rows: Record<string, unknown>[],
  filename: string
) {
  const csv = toCSV(columns, rows);
  downloadText(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
}

/** Export data as a print-optimised PDF via browser print dialog */
export function exportPDF(
  title: string,
  subtitle: string,
  columns: ExportColumn[],
  rows: Record<string, unknown>[]
) {
  const now = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const tableRows = rows
    .map(
      (row) =>
        `<tr>${columns
          .map((c) => {
            const val = row[c.key] ?? '';
            const str = Array.isArray(val) ? val.join(', ') : String(val);
            return `<td>${str}</td>`;
          })
          .join('')}</tr>`
    )
    .join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; margin: 24px; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    p.sub { font-size: 11px; color: #555; margin: 0 0 16px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1e3a5f; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
    tr:nth-child(even) td { background: #f9fafb; }
    .footer { margin-top: 16px; font-size: 10px; color: #888; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <p class="sub">${subtitle} &nbsp;·&nbsp; Generated: ${now} &nbsp;·&nbsp; City of Pleasanton TPRM</p>
  <table>
    <thead><tr>${columns.map((c) => `<th>${c.header}</th>`).join('')}</tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
  <p class="footer">This document is generated for compliance reporting and municipal record-keeping purposes only.</p>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}
